import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import blogService from '../../services/blogService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import RichTextEditor from './components/RichTextEditor';
import ImageUploader from './components/ImageUploader';
import TagInput from './components/TagInput';

const BlogArticleEditor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, userProfile } = useAuth();
  const articleId = searchParams?.get('id');
  const isEditMode = !!articleId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    topic: 'personal_finance',
    status: 'draft',
    featuredImageUrl: '',
    videoEmbedUrl: '',
    tags: [],
    comingSoon: false,
    featured: false
  });

  // Check admin access - user must be demo@rovynbudget.com or rovyngroupinc@gmail.com
  useEffect(() => {
    if (userProfile && userProfile?.email !== 'demo@rovynbudget.com' && userProfile?.email !== 'rovyngroupinc@gmail.com') {
      navigate('/financial-blog');
    }
  }, [userProfile, navigate]);

  // Load article if editing
  useEffect(() => {
    if (isEditMode && articleId) {
      loadArticle();
    }
  }, [articleId]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      setError('');
      const article = await blogService?.getArticleById(articleId);
      
      // Load tags for the article
      const tags = await blogService?.getArticleTags(articleId);
      
      setFormData({
        title: article?.title || '',
        excerpt: article?.excerpt || '',
        content: article?.content || '',
        topic: article?.topic || 'personal_finance',
        status: article?.status || 'draft',
        featuredImageUrl: article?.featuredImageUrl || '',
        videoEmbedUrl: article?.videoEmbedUrl || '',
        tags: tags || [],
        comingSoon: article?.comingSoon || false,
        featured: article?.featured || false
      });
    } catch (err) {
      setError(err?.message || 'Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData?.title?.trim()) {
      errors.title = 'Title is required';
    } else if (formData?.title?.trim()?.length < 10) {
      errors.title = 'Title must be at least 10 characters';
    }
    
    if (!formData?.excerpt?.trim()) {
      errors.excerpt = 'Excerpt is required';
    } else if (formData?.excerpt?.trim()?.length < 50) {
      errors.excerpt = 'Excerpt must be at least 50 characters';
    }
    
    if (!formData?.content?.trim() || formData?.content?.trim() === '<p><br></p>') {
      errors.content = 'Content is required';
    } else if (formData?.content?.trim()?.length < 100) {
      errors.content = 'Content must be at least 100 characters';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleSave = async (publishNow = false) => {
    setError('');
    setFieldErrors({});

    // Validation
    if (!validateForm()) {
      setError('Please fix the errors below before saving');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setSaving(true);
      
      // Generate slug from title
      const slug = formData?.title
        ?.toLowerCase()
        ?.replace(/[^a-z0-9]+/g, '-')
        ?.replace(/^-+|-+$/g, '');

      const dataToSave = {
        authorId: user?.id,
        title: formData?.title?.trim(),
        slug: slug,
        excerpt: formData?.excerpt?.trim(),
        content: formData?.content,
        topic: formData?.topic,
        status: publishNow ? 'published' : formData?.status,
        featuredImageUrl: formData?.featuredImageUrl,
        videoEmbedUrl: formData?.videoEmbedUrl,
        comingSoon: formData?.comingSoon,
        featured: formData?.featured,
        readingTimeMinutes: Math.ceil(formData?.content?.replace(/<[^>]*>/g, '')?.split(/\s+/)?.length / 200)
      };

      let savedArticleId = articleId;
      
      if (isEditMode) {
        await blogService?.updateArticle(articleId, dataToSave);
      } else {
        const newArticle = await blogService?.createArticle(dataToSave);
        savedArticleId = newArticle?.id;
      }

      // Save tags
      if (formData?.tags?.length > 0) {
        await blogService?.saveArticleTags(savedArticleId, formData?.tags);
      }

      // Show success message and navigate
      alert(publishNow ? 'Article published successfully!' : 'Article saved as draft!');
      navigate('/financial-blog');
    } catch (err) {
      setError(err?.message || 'Failed to save article');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (file) => {
    try {
      setUploadingImage(true);
      setError('');
      const imageUrl = await blogService?.uploadImage(file, user?.id);
      setFormData({ ...formData, featuredImageUrl: imageUrl });
    } catch (err) {
      setError(err?.message || 'Failed to upload image');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, featuredImageUrl: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading article...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-16 sm:px-24 py-16">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-16">
              <button
                onClick={() => navigate('/financial-blog')}
                className="p-8 hover:bg-muted rounded-lg transition-colors"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-foreground">
                  {isEditMode ? 'Edit Article' : 'Create New Article'}
                </h1>
                <p className="text-sm text-muted-foreground">Rovyn Financial Blog</p>
              </div>
            </div>
            <div className="flex items-center gap-12">
              <Button
                variant="outline"
                onClick={() => handleSave(false)}
                loading={saving}
                disabled={saving || uploadingImage}
              >
                Save Draft
              </Button>
              <Button
                onClick={() => handleSave(true)}
                loading={saving}
                disabled={saving || uploadingImage}
                iconName="Send"
              >
                Publish
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-16 sm:px-24 py-32">
        {error && (
          <div className="mb-24 p-16 bg-destructive/10 border border-destructive rounded-lg">
            <div className="flex items-start gap-12">
              <Icon name="AlertCircle" size={20} className="text-destructive mt-2" />
              <div className="flex-1">
                <p className="text-destructive font-medium">{error}</p>
                {Object.keys(fieldErrors)?.length > 0 && (
                  <ul className="mt-8 space-y-4 text-sm text-destructive">
                    {Object.entries(fieldErrors)?.map(([field, message]) => (
                      <li key={field}>• {message}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {uploadingImage && (
          <div className="mb-24 p-16 bg-primary/10 border border-primary rounded-lg">
            <div className="flex items-center gap-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
              <p className="text-primary font-medium">Uploading image...</p>
            </div>
          </div>
        )}

        <div className="space-y-32">
          {/* Title */}
          <div>
            <Input
              label="Article Title"
              type="text"
              value={formData?.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e?.target?.value });
                if (fieldErrors?.title) {
                  setFieldErrors({ ...fieldErrors, title: undefined });
                }
              }}
              placeholder="Enter article title..."
              required
              className="text-2xl font-semibold"
            />
            {fieldErrors?.title && (
              <p className="mt-4 text-sm text-destructive">{fieldErrors?.title}</p>
            )}
          </div>

          {/* Metadata Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            {/* Topic */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Topic *
              </label>
              <select
                value={formData?.topic}
                onChange={(e) => setFormData({ ...formData, topic: e?.target?.value })}
                className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="personal_finance">Personal Finance</option>
                <option value="investment_tips">Investment Tips</option>
                <option value="budgeting_strategies">Budgeting Strategies</option>
                <option value="market_analysis">Market Analysis</option>
                <option value="credit_management">Credit Management</option>
                <option value="debt_management">Debt Management</option>
                <option value="saving_strategies">Saving Strategies</option>
                <option value="financial_wellness">Financial Wellness</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-8">
                Status
              </label>
              <select
                value={formData?.status}
                onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          {/* Excerpt */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Excerpt *
            </label>
            <textarea
              value={formData?.excerpt}
              onChange={(e) => {
                setFormData({ ...formData, excerpt: e?.target?.value });
                if (fieldErrors?.excerpt) {
                  setFieldErrors({ ...fieldErrors, excerpt: undefined });
                }
              }}
              placeholder="Brief summary of the article (shown in article cards)..."
              rows={3}
              className="w-full px-16 py-12 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
            {fieldErrors?.excerpt && (
              <p className="mt-4 text-sm text-destructive">{fieldErrors?.excerpt}</p>
            )}
          </div>

          {/* Featured Image */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Featured Image
            </label>
            <ImageUploader
              imageUrl={formData?.featuredImageUrl}
              onUpload={handleImageUpload}
              onRemove={handleRemoveImage}
            />
          </div>

          {/* YouTube Video Embed */}
          <div>
            <Input
              label="YouTube Video URL (Optional)"
              type="url"
              value={formData?.videoEmbedUrl}
              onChange={(e) => setFormData({ ...formData, videoEmbedUrl: e?.target?.value })}
              placeholder="https://www.youtube.com/watch?v=..."
              description="Paste a YouTube video URL to embed it in the article"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Tags
            </label>
            <TagInput
              tags={formData?.tags}
              onChange={(tags) => setFormData({ ...formData, tags })}
            />
          </div>

          {/* Content Editor */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-8">
              Article Content *
            </label>
            <RichTextEditor
              value={formData?.content}
              onChange={(content) => {
                setFormData({ ...formData, content });
                if (fieldErrors?.content) {
                  setFieldErrors({ ...fieldErrors, content: undefined });
                }
              }}
            />
            {fieldErrors?.content && (
              <p className="mt-4 text-sm text-destructive">{fieldErrors?.content}</p>
            )}
          </div>

          {/* Article Labels */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            {/* Coming Soon */}
            <div className="flex items-center gap-12 p-16 bg-card border border-input rounded-lg">
              <input
                type="checkbox"
                id="comingSoon"
                checked={formData?.comingSoon}
                onChange={(e) => setFormData({ ...formData, comingSoon: e?.target?.checked })}
                className="w-16 h-16 text-primary bg-background border-input rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="comingSoon" className="flex-1 cursor-pointer">
                <div className="text-sm font-medium text-foreground">Coming Soon</div>
                <div className="text-xs text-muted-foreground">Mark this article as coming soon</div>
              </label>
            </div>

            {/* Featured */}
            <div className="flex items-center gap-12 p-16 bg-card border border-input rounded-lg">
              <input
                type="checkbox"
                id="featured"
                checked={formData?.featured}
                onChange={(e) => setFormData({ ...formData, featured: e?.target?.checked })}
                className="w-16 h-16 text-primary bg-background border-input rounded focus:ring-2 focus:ring-primary"
              />
              <label htmlFor="featured" className="flex-1 cursor-pointer">
                <div className="text-sm font-medium text-foreground">Featured</div>
                <div className="text-xs text-muted-foreground">Highlight this article as featured</div>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-12 pt-24 border-t border-border">
            <Button
              variant="outline"
              onClick={() => navigate('/financial-blog')}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSave(false)}
              loading={saving}
              disabled={saving}
            >
              Save Draft
            </Button>
            <Button
              onClick={() => handleSave(true)}
              loading={saving}
              disabled={saving}
              iconName="Send"
            >
              Publish Article
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogArticleEditor;